import { useTranslation } from 'react-i18next'

import { chakra } from '@enechain/ecloud-designsystem'
import {
  FaArrowFromLineRegular,
  FaArrowToLineRegular,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import { FILTER_RAIL_WIDTH } from '~/pages/SpotBidding/constants/filterSidebar'
import { LIST_HEADER_HEIGHT } from '~/pages/SpotBidding/constants/listHeader'

/**
 * 色が付く視覚ボックスの一辺。 当たり判定 (48x56) より小さくする。
 * 8080 の side menu トグル `.sidebar-toggle { width: 32px; height: 32px }` と同値。
 */
const TOGGLE_BOX_SIZE = '32px'

type Props = {
  /** サイドバーが開いているか。 アイコンの向きとアクセシブル名を決める。 */
  isOpen: boolean
  /** 押した時 (開閉の反転)。 両状態で同じ handler を渡す。 */
  onToggle: () => void
}

/**
 * フィルターサイドバーの開閉トグル (通常入札 / ブロック入札 共用)。
 *
 * ## 開く導線と閉じる導線を同じ場所に置く
 *
 * 2026-09-15 デザイナー指摘「折りたたむ導線は、 広げる導線と同じ場所に配置して、
 * 開閉を連打できるようにしたい」。 それまでは
 *
 * - **開く** = レール (サイドバー列の左上)
 * - **閉じる** = 一覧ヘッダ右側の「フィルターを折りたたむ」 ボタン
 *
 * と画面の左右に分かれていたため、 一度開閉するたびにポインタを大きく動かす
 * 必要があり、 開閉を往復して見比べることができなかった。 本コンポーネントを
 * 両状態で**同じ座標**に置くことで、 同じ場所を連打できる。
 *
 * ## 座標が一致する根拠
 *
 * 畳んだ時のレール幅と、 開いた時に見出し行の左端が占める幅をどちらも 48px
 * (`FILTER_RAIL_WIDTH`) に、 高さは両状態とも見出し行と同じ 56px
 * (`LIST_HEADER_HEIGHT`) に揃えている。 アイコンは縦横とも中央寄せなので、
 * 開閉どちらでも**アイコン中心はサイドバー列の左端から 24px / 上端から 28px**。
 *
 * 開いている時は見出し行 (`SidebarFilterHeader`) の内側に入るが、 見出し行は
 * `padding` を持つので、 そのままだと 48px の枠が内側に寄ってアイコンが右へ
 * ずれる。 負 margin で padding を打ち消して列の左端まで届かせている
 * (`SidebarFilterHeader` の `leading`)。
 *
 * ## アイコンは「押すと何が起きるか」 を描く
 *
 * 8080 の side menu (`include-nav.js`: `collapsed ? EXPAND_ICON : COLLAPSE_ICON`)
 * と同じ向き。 開いている時は畳む側の `FaArrowFromLineRegular` (←|)、 畳んで
 * いる時は開く側の `FaArrowToLineRegular` (→|) を出す。 現在の状態ではなく
 * 操作の結果を描くので、 連打しても向きが「次に起きること」 を指し続ける。
 */
export const FilterToggleButton = ({
  isOpen,
  onToggle,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const Icon = isOpen ? FaArrowFromLineRegular : FaArrowToLineRegular

  return (
    // DS `Flex` に `as="button"` を当てても `FlexProps` は div 前提のままで
    // `type` を受け取れない (tsc が TS2322 で落ちる)。 素の `chakra.button` を
    // flex で並べる。
    <chakra.button
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      display="flex"
      width={FILTER_RAIL_WIDTH}
      height={LIST_HEADER_HEIGHT}
      color="Text.midEmphasis"
      // `_hover` にネストセレクタを書いても chakra は展開せず、 背景が button
      // 自身 (48x56) に当たってしまう (実測済み)。 ネストは `css` へ書く
      // (`BottomMenu` / `AdminOcctoMaster` と同じ書き方)。
      cursor="pointer"
      aria-expanded={isOpen}
      // 当たり判定は 48x56 (レールと座標を揃えるため) だが、 **色が付くのは内側の
      // 32x32** にする。 8080 の side menu トグル (`css/layout.css` の
      // `.sidebar-toggle`) が 32x32 + border-radius で、 48x56 全面を塗ると
      // side menu より重い当たり方になる。
      aria-label={
        isOpen
          ? t('page:biddingSpot.sidebar.filterCollapse')
          : t('page:biddingSpot.sidebar.filterExpand')
      }
      css={{
        '&:hover > span': { backgroundColor: 'Hover.lowEmphasis' },
        '&:active > span': { backgroundColor: 'Press.lowEmphasis' },
      }}
      onClick={onToggle}
      type="button"
    >
      <chakra.span
        alignItems="center"
        justifyContent="center"
        display="flex"
        width={TOGGLE_BOX_SIZE}
        height={TOGGLE_BOX_SIZE}
        borderRadius="base"
        // 8080 `.sidebar-toggle { transition: background-color 0.15s }` と同値。
        transition="background-color 0.15s ease"
      >
        {/* gridInline (16px) は「表・グリッド内のインライン icon」 の役割なので
            使わない。 サイドバーの主導線なので labeledButton (20px)。 */}
        <Icon boxSize={iconSize.labeledButton} />
      </chakra.span>
    </chakra.button>
  )
}
